import { UserPreference } from "igfd-ui/src/app-index";

import { datadogRumConfig } from "../environment/monitoring";
import { getAnonymousUserId } from "./user";

export type RestaurantAnalyticsCtx = {
  uuid: string; // restaurant uuid

  // additional information returned for use in analytics
  name: string; // This is the actual restaurant regardless of the location.
  analyticsName: string; // A more identifiable name of the restaurant along with the location.
  source: string | null; // How the user landed here, this is a query param `source` in the URL if present.
  locationId?: string;
  menuId?: string;
};

// User preference that is stable and can be used for analytics
type UserPreferenceAnalyticsCtx = UserPreference & {
  anonymousUserId: string;
};

const isOnDevMode = () => process.env.MODE === "development";

// Skipped in dev and if cookie consent is not given or not set (undefined)
const isDdEnabled = () => !isOnDevMode();

export const startMonitoring = async () => {
  if (isDdEnabled()) {
    // This adds roughly 140kb to the bundle size (as of v4.46.0)
    // We are currently on v5.20.0
    const brwoserRum = await import("@datadog/browser-rum");
    const datadogRum = brwoserRum.datadogRum;

    datadogRum.init(datadogRumConfig);
    datadogRum.setUserProperty("anonymousUserId", getAnonymousUserId());
    datadogRum.startSessionReplayRecording();
    datadogRum.setTrackingConsent("granted");
  }
};

// When a restaurant uuid is known, we set it in the analytics context
// As of now this is limited to the current session and page.
// We do not share this context with other sessions (different tabs / etc)
// https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#contexts-life-cycle
export const setRestaurantCtxForMonitoring = async (
  restaurantAnalyticsCtx: RestaurantAnalyticsCtx
) => {
  if (isDdEnabled()) {
    const brwoserRum = await import("@datadog/browser-rum");
    const datadogRum = brwoserRum.datadogRum;

    datadogRum.setGlobalContextProperty("restaurant", restaurantAnalyticsCtx);
  }
};

// Custom action
const addRumAction: (
  actionName: string,
  ctx: object | undefined
) => void = async (actionName, data) => {
  if (isDdEnabled()) {
    const brwoserRum = await import("@datadog/browser-rum");
    const datadogRum = brwoserRum.datadogRum;

    datadogRum.addAction(actionName, data);
  }
  if (isOnDevMode()) {
    console.log(`RUM Action: ${actionName}`, data);
  }
};
